import type {ReactNode} from "react";
import {useCallback, useEffect, useRef, useState} from "react";
import {useBlocker} from "@tanstack/react-router";
import {DiscardUnsavedChangesModal} from "~/components/discardUnsavedChangesModal";

export interface UnsavedChangesGuard {
    /**
     * Call from an explicit close affordance (Cancel button, X icon, Escape,
     * backdrop). If the form is dirty this opens the confirmation modal;
     * otherwise it runs `onDiscard` immediately.
     */
    requestClose: () => void;
    /**
     * Run an intentional navigation (e.g. after a successful submit) without
     * tripping the guard. The wrapped callback executes immediately with
     * blocking temporarily suppressed.
     */
    allowNavigation: (navigateFn: () => void) => void;
    /** Render this somewhere in the consumer's tree (once). */
    modal: ReactNode;
    /** Current dirty state (mirrors the `isDirty` input). */
    isDirty: boolean;
    /** Re-baseline the snapshot guard so the current values are treated as clean. */
    markClean?: () => void;
}

/**
 * UI-agnostic unsaved-changes guard.
 *
 * Owns:
 * - the confirmation modal open/close state,
 * - in-app navigation blocking (browser back/forward, breadcrumb `<Link>`,
 *   `navigate()`) via TanStack Router's `useBlocker`, and
 * - tab close / refresh / hard navigation via the native `beforeunload` event.
 *
 * The modal can be triggered by EITHER an explicit `requestClose()` (Cancel/X)
 * OR the router blocker firing (back/forward/breadcrumb). The Discard/Cancel
 * handlers resolve against whichever source is active.
 *
 * Intentional navigations (confirmed discard, post-submit redirect) are routed
 * through a bypass ref so they are not re-intercepted by the blocker while the
 * form is still technically dirty. The bypass auto-resets so a long-lived guard
 * (e.g. the shared edit-panel context) keeps guarding subsequent edits.
 */
export function useUnsavedChangesGuard({
    isDirty,
    onDiscard,
}: {
    isDirty: boolean;
    /** Runs when the user confirms discarding (or closes while clean). */
    onDiscard: () => void;
}): UnsavedChangesGuard {
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Keep a ref in sync so `shouldBlockFn` (called imperatively at navigation
    // time) always sees the latest dirty state.
    const isDirtyRef = useRef(isDirty);
    isDirtyRef.current = isDirty;

    // When true, the blocker lets a single intentional navigation through.
    const bypassRef = useRef(false);

    // In-app navigation blocking (browser back/forward, breadcrumb <Link>, navigate()).
    // `enableBeforeUnload` must be a function: TanStack treats boolean `true` as
    // "always warn on refresh", ignoring `shouldBlockFn` / dirty state.
    const shouldBlock = () => isDirtyRef.current === true && bypassRef.current === false;
    const blocker = useBlocker({
        shouldBlockFn: shouldBlock,
        enableBeforeUnload: shouldBlock,
        withResolver: true,
    });

    useEffect(() => {
        if (blocker.status === "blocked") {
            setIsModalOpen(true);
        }
    }, [blocker.status]);

    // Tab close / refresh / hard navigation.
    useEffect(() => {
        const handler = (event: BeforeUnloadEvent) => {
            if (isDirty === true) {
                event.preventDefault();
                // Legacy browsers require returnValue to be set to trigger the prompt.
                event.returnValue = "";
            }
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [isDirty]);

    const runWithoutBlocking = useCallback((fn: () => void) => {
        bypassRef.current = true;
        try {
            fn();
        } finally {
            // Reset after the pending navigation resolves so a persistent guard
            // keeps blocking future edits.
            window.setTimeout(() => {
                bypassRef.current = false;
            }, 0);
        }
    }, []);

    const requestClose = useCallback(() => {
        if (isDirtyRef.current === true) {
            setIsModalOpen(true);
        } else {
            onDiscard();
        }
    }, [onDiscard]);

    const handleDiscard = useCallback(() => {
        setIsModalOpen(false);
        if (blocker.status === "blocked") {
            blocker.proceed();
        } else {
            runWithoutBlocking(onDiscard);
        }
    }, [blocker, onDiscard, runWithoutBlocking]);

    const handleCancel = useCallback(() => {
        setIsModalOpen(false);
        if (blocker.status === "blocked") {
            blocker.reset();
        }
    }, [blocker]);

    const allowNavigation = useCallback((navigateFn: () => void) => {
        setIsModalOpen(false);
        bypassRef.current = true;
        isDirtyRef.current = false;
        try {
            navigateFn();
        } finally {
            // Keep bypass active through the next frame so async router navigations
            // triggered by submit success are not re-intercepted while still dirty.
            window.setTimeout(() => {
                bypassRef.current = false;
            }, 100);
        }
    }, []);

    const modal = (
        <DiscardUnsavedChangesModal
            isOpen={isModalOpen}
            onCancel={handleCancel}
            onDiscard={handleDiscard}
        />
    );

    return {requestClose, allowNavigation, modal, isDirty};
}
